<template>
  <div class="tpc-card">

    <div class="tpc-header">
      <div class="tpc-header-left">
        <i class="ti ti-sparkles" aria-hidden="true"></i>
        <span>Payroll anomaly check</span>
      </div>
      <span class="tpc-sub">Latest 2 runs compared · Live</span>
    </div>

    <!-- All clear -->
    <div v-if="data.total === 0" class="tpc-clear">
      <i class="ti ti-circle-check" aria-hidden="true" style="font-size:28px;color:#3B6D11;display:block;margin-bottom:8px"></i>
      <div style="font-weight:500;color:#27500A">All clear</div>
      <div style="font-size:11px;color:#639922;margin-top:4px">No anomalies detected in the latest finalized payroll run.</div>
    </div>

    <template v-else>
      <!-- Summary metrics -->
      <div class="tpc-metrics">
        <div class="tpc-metric">
          <div class="tpc-val" :style="{ color: data.high > 0 ? '#A32D2D' : 'inherit' }">{{ data.high }}</div>
          <div class="tpc-label">High</div>
        </div>
        <div class="tpc-metric">
          <div class="tpc-val" :style="{ color: data.medium > 0 ? '#854F0B' : 'inherit' }">{{ data.medium }}</div>
          <div class="tpc-label">Medium</div>
        </div>
        <div class="tpc-metric">
          <div class="tpc-val">{{ data.low }}</div>
          <div class="tpc-label">Low</div>
        </div>
        <div class="tpc-metric">
          <div class="tpc-val">{{ data.total }}</div>
          <div class="tpc-label">Total</div>
        </div>
      </div>

      <!-- Anomaly type pills -->
      <div class="tpc-type-row">
        <div v-if="data.byType.duplicate.length"   class="tpc-type-pill tpc-pill-high">
          <i class="ti ti-copy" aria-hidden="true"></i> {{ data.byType.duplicate.length }} duplicate
        </div>
        <div v-if="data.byType.variance.length"    class="tpc-type-pill tpc-pill-med">
          <i class="ti ti-trending-up" aria-hidden="true"></i> {{ data.byType.variance.length }} variance
        </div>
        <div v-if="data.byType.ot_spike.length"    class="tpc-type-pill tpc-pill-med">
          <i class="ti ti-clock" aria-hidden="true"></i> {{ data.byType.ot_spike.length }} OT spike
        </div>
        <div v-if="data.byType.hc_mismatch.length" class="tpc-type-pill tpc-pill-med">
          <i class="ti ti-users" aria-hidden="true"></i> {{ data.byType.hc_mismatch.length }} headcount
        </div>
        <div v-if="data.byType.missing.length"     class="tpc-type-pill tpc-pill-low">
          <i class="ti ti-user-x" aria-hidden="true"></i> {{ data.byType.missing.length }} missing
        </div>
        <div v-if="data.byType.pay_item_new && data.byType.pay_item_new.length" class="tpc-type-pill tpc-pill-high">
          <i class="ti ti-plus" aria-hidden="true"></i> {{ data.byType.pay_item_new.length }} new pay item
        </div>
        <div v-if="data.byType.pay_item_dropped && data.byType.pay_item_dropped.length" class="tpc-type-pill tpc-pill-med">
          <i class="ti ti-minus" aria-hidden="true"></i> {{ data.byType.pay_item_dropped.length }} dropped pay item
        </div>
      </div>

      <!-- Anomaly rows -->
      <div class="tpc-list">
        <div
          v-for="(a, i) in showAll ? data.all : data.all.slice(0, 8)"
          :key="i"
          class="tpc-row"
        >
          <div class="tpc-icon-wrap" :class="iconClass(a.severity)">
            <i :class="typeIcon(a.anomaly_type)" aria-hidden="true"></i>
          </div>
          <div class="tpc-row-info">
            <div class="tpc-row-title">
              {{ typeLabel(a.anomaly_type) }}
              <span v-if="a.affected_psnname"> — {{ a.affected_psnname }}</span>
            </div>
            <div class="tpc-row-sub">
              {{ a.payoutdesc }} · {{ a.pyyear }}/{{ String(a.pymonth).padStart(2,'0') }}
            </div>
            <div class="tpc-row-detail" v-if="a.detail">{{ a.detail }}</div>
            <div v-if="a.staff_details && a.staff_details.length" class="tpc-staff-details">
              <div
                v-for="(st, si) in a.staff_details.slice(0, 5)"
                :key="'st'+si"
                class="tpc-staff-row"
              >
                <span class="tpc-staff-name">{{ st.psnname }}</span>
                <span class="tpc-staff-amt">RM {{ parseFloat(st.aprvamt).toFixed(2) }}</span>
              </div>
              <div v-if="a.staff_details.length > 5" class="tpc-staff-more">
                +{{ a.staff_details.length - 5 }} more staff
              </div>
            </div>
          </div>
          <div class="tpc-variance" v-if="a.variance_pct !== null">
            <span :style="{ color: Math.abs(a.variance_pct) >= 20 ? '#A32D2D' : '#854F0B' }">
              {{ a.variance_pct > 0 ? '+' : '' }}{{ a.variance_pct }}%
            </span>
          </div>
          <span class="tpc-badge" :class="badgeClass(a.severity)">
            {{ a.severity }}
          </span>
        </div>
      </div>

      <div v-if="!showAll && data.total > 8" class="tpc-more">
        +{{ data.total - 8 }} more — click 'Show all' to expand
      </div>
    </template>

    <!-- Footer -->
    <div class="tpc-footer">
      <button class="tpc-btn" @click="showAll = !showAll">
        {{ showAll ? 'Show less ↑' : 'Show all ' + data.total + ' ↓' }}
      </button>
      <button class="tpc-btn" @click="askMoreWithContext()">
        What to fix first ↗
      </button>
    </div>

  </div>
</template>

<script>
export default {
  name: "TaraPayrollCard",
  props: { data: { type: Object, required: true } },
  data() { return { showAll: false }; },
  methods: {
    typeLabel(type) {
      const map = { VARIANCE: 'Payout variance', OT_SPIKE: 'OT spike', HC_MISMATCH: 'Headcount mismatch', MISSING: 'Missing staff', DUPLICATE: 'Duplicate record', PAY_ITEM_NEW: 'New pay item', PAY_ITEM_DROPPED: 'Dropped pay item' };
      return map[type] || type;
    },
    typeIcon(type) {
      const map = { VARIANCE: 'ti ti-trending-up', OT_SPIKE: 'ti ti-clock-exclamation', HC_MISMATCH: 'ti ti-users', MISSING: 'ti ti-user-x', DUPLICATE: 'ti ti-copy', PAY_ITEM_NEW: 'ti ti-plus', PAY_ITEM_DROPPED: 'ti ti-minus' };
      return (map[type] || 'ti ti-alert') + ' ti';
    },
    iconClass(severity) {
      if (severity === 'HIGH')   return 'tpc-icon-high';
      if (severity === 'MEDIUM') return 'tpc-icon-med';
      return 'tpc-icon-low';
    },
    badgeClass(severity) {
      if (severity === 'HIGH')   return 'tpc-badge-high';
      if (severity === 'MEDIUM') return 'tpc-badge-med';
      return 'tpc-badge-low';
    },
    askMore(text) { this.$emit('ask', text); },
    askMoreWithContext() {
      // Build a summary of anomalies to include in the question
      const lines = [];
      if (this.data.byType.duplicate && this.data.byType.duplicate.length)
        lines.push(`${this.data.byType.duplicate.length} duplicate record(s)`);
      if (this.data.byType.variance && this.data.byType.variance.length)
        lines.push(`${this.data.byType.variance.length} payout variance(s)`);
      if (this.data.byType.ot_spike && this.data.byType.ot_spike.length)
        lines.push(`${this.data.byType.ot_spike.length} OT spike(s)`);
      if (this.data.byType.hc_mismatch && this.data.byType.hc_mismatch.length)
        lines.push(`${this.data.byType.hc_mismatch.length} headcount mismatch(es)`);
      if (this.data.byType.missing && this.data.byType.missing.length)
        lines.push(`${this.data.byType.missing.length} missing staff`);
      if (this.data.byType.pay_item_new && this.data.byType.pay_item_new.length)
        lines.push(`${this.data.byType.pay_item_new.length} new pay item(s) not in previous run`);
      if (this.data.byType.pay_item_dropped && this.data.byType.pay_item_dropped.length)
        lines.push(`${this.data.byType.pay_item_dropped.length} pay item(s) dropped vs previous run`);
      const summary = lines.join(', ');
      this.$emit('ask', `My payroll check found: ${summary}. Please advise which issue to address first and the steps to resolve each one.`);
    },
  },
};
</script>

<style scoped>
.tpc-card { border:0.5px solid #AFA9EC; border-radius:12px; background:#FAFAFE; overflow:hidden; font-family:inherit; font-size:13px; margin:4px 0 8px; max-width:440px; }
.tpc-header { display:flex; align-items:center; justify-content:space-between; padding:10px 14px 8px; background:#EEEDFE; border-bottom:0.5px solid #AFA9EC; }
.tpc-header-left { display:flex; align-items:center; gap:6px; font-weight:500; color:#3C3489; font-size:13px; }
.tpc-header-left i { font-size:15px; }
.tpc-sub { font-size:11px; color:#534AB7; }
.tpc-clear { padding:20px 14px; text-align:center; }
.tpc-metrics { display:grid; grid-template-columns:repeat(4,1fr); border-bottom:0.5px solid #E5E3F8; }
.tpc-metric { padding:10px 8px; text-align:center; border-right:0.5px solid #E5E3F8; }
.tpc-metric:last-child { border-right:none; }
.tpc-val { font-size:20px; font-weight:500; color:#26215C; }
.tpc-label { font-size:10px; color:#7F77DD; margin-top:2px; }
.tpc-type-row { display:flex; flex-wrap:wrap; gap:6px; padding:10px 14px 6px; border-bottom:0.5px solid #E5E3F8; }
.tpc-type-pill { display:flex; align-items:center; gap:4px; font-size:11px; font-weight:500; padding:3px 8px; border-radius:20px; }
.tpc-type-pill i { font-size:12px; }
.tpc-pill-high { background:#FCEBEB; color:#791F1F; }
.tpc-pill-med  { background:#FAEEDA; color:#633806; }
.tpc-pill-low  { background:#E6F1FB; color:#0C447C; }
.tpc-list { padding:4px 0; }
.tpc-row { display:flex; align-items:flex-start; gap:10px; padding:9px 14px; border-bottom:0.5px solid #F0EFF9; }
.tpc-row:last-child { border-bottom:none; }
.tpc-icon-wrap { width:28px; height:28px; border-radius:6px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; margin-top:1px; }
.tpc-icon-high { background:#FCEBEB; color:#791F1F; }
.tpc-icon-med  { background:#FAEEDA; color:#633806; }
.tpc-icon-low  { background:#E6F1FB; color:#0C447C; }
.tpc-row-info { flex:1; min-width:0; }
.tpc-row-title { font-size:12px; font-weight:500; color:#26215C; }
.tpc-row-sub   { font-size:10px; color:#7F77DD; margin-top:1px; }
.tpc-row-detail { font-size:11px; color:#5F5E5A; margin-top:2px; }
.tpc-variance { font-size:13px; font-weight:500; flex-shrink:0; padding-top:2px; }
.tpc-badge { font-size:10px; font-weight:500; padding:2px 6px; border-radius:20px; flex-shrink:0; white-space:nowrap; margin-top:2px; }
.tpc-badge-high { background:#FCEBEB; color:#791F1F; }
.tpc-badge-med  { background:#FAEEDA; color:#633806; }
.tpc-badge-low  { background:#EAF3DE; color:#27500A; }
.tpc-more { padding:8px 14px; font-size:11px; color:#7F77DD; text-align:center; border-top:0.5px solid #E5E3F8; }
.tpc-staff-details { margin-top:6px; padding:6px 8px; background:#F8F8FF; border-radius:4px; }
.tpc-staff-row { display:flex; justify-content:space-between; padding:2px 0; font-size:11px; }
.tpc-staff-name { color:#26215C; }
.tpc-staff-amt { color:#534AB7; font-weight:500; }
.tpc-staff-more { font-size:10px; color:#7F77DD; margin-top:3px; }
.tpc-footer { display:flex; gap:8px; padding:10px 14px; background:#F5F4FD; border-top:0.5px solid #E5E3F8; }
.tpc-btn { font-size:12px; padding:5px 10px; border-radius:6px; border:0.5px solid #AFA9EC; background:white; color:#3C3489; cursor:pointer; }
.tpc-btn:hover { background:#EEEDFE; }
</style>